import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations';
import logo from '../assets/logo-optimized.webp';
import {
  FaLinkedinIn,
  FaFacebookF,
  FaTiktok,
  FaSnapchat,
  FaInstagram,
  FaYoutube,
} from 'react-icons/fa';

const Footer = () => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = translations[language];

  const socialLinks = [
    { icon: FaFacebookF, url: 'https://www.facebook.com/connecto.business', label: 'Facebook' },
    { icon: FaLinkedinIn, url: 'https://www.linkedin.com/company/connecto-me/posts/?feedView=all', label: 'LinkedIn' },
    { icon: FaTiktok, url: 'https://www.tiktok.com/@connectoco', label: 'TikTok' },
    { icon: FaSnapchat, url: 'https://www.snapchat.com/add/connectodigital', label: 'Snapchat' },
    { icon: FaInstagram, url: 'https://www.instagram.com/connecto.digital', label: 'Instagram' },
    { icon: FaYoutube, url: 'https://www.youtube.com/channel/UCMasx3Wn4jn89xgbPemBqig', label: 'YouTube' },
  ];

  const quickLinks = [
    { text: isAr ? 'افتح حساب' : 'Account Setup', link: '/service/account-setup/' },
    { text: isAr ? 'إدارة الحسابات' : 'Account Management', link: '/service/account-management/' },
    { text: isAr ? 'منصة كونكتو' : 'Connecto Platform', link: '/service/platform/' },
    { text: isAr ? 'المنصات المدعومة' : 'Connected Marketplace', link: '/supported-marketplaces/' },
  ];

  const companyLinks = [
    { text: isAr ? 'من نحن' : 'About Us', link: '/about/' },
    { text: isAr ? 'الأسعار' : 'Pricing', link: '/pricing/' },
    { text: isAr ? 'سياسة الخصوصية' : 'Privacy Policy', link: '/privacy/' },
  ];

  const supportLinks = [
    { text: isAr ? 'اتصل بنا' : 'Contact Us', link: '/partners/' },
    { text: isAr ? 'الشروط والأحكام' : 'Terms & Conditions', link: '/privacy/' },
    { text: isAr ? 'الدعم' : 'Support', link: '/support/' },
  ];

  return (
    <footer
      className="pt-20 sm:pt-12 pb-8 bg-dark-bg"
      dir={isAr ? 'rtl' : 'ltr'}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12 mb-12">
          {/* Brand & Description */}
          <div className={`flex flex-col gap-4 items-center sm:items-start ${isAr ? 'sm:text-right' : 'sm:text-left'} text-center`}>
            <img
              src={logo}
              alt="Connecto"
              loading="lazy"
              width="300"
              height="106"
              className="h-10 w-fit brightness-0 invert mx-auto sm:mx-0"
            />

            <p
              className={`text-body-footer ${isAr ? 'sm:text-right' : 'sm:text-left'} text-center text-sm font-almarai leading-relaxed`}
            >
              {t.footerDescription}
            </p>

            <div className="flex justify-center sm:justify-start gap-3 mt-2">
              {socialLinks.map((social, index) => {
                const Icon = social.icon;
                return (
                  <a
                    key={index}
                    href={social.url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-white hover:opacity-80 transition-opacity"
                    aria-label={social.label}
                  >
                    <Icon size={20} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links Column */}
          <div className={`${isAr ? 'sm:text-right' : 'sm:text-left'} text-center`}>
            <h3 className="mb-6 text-white font-ibm font-bold text-lg">{isAr ? 'روابط سريعة' : 'Quick Links'}</h3>
            <ul className="space-y-3">
              {quickLinks.map((item, i) => (
                <li key={i}>
                  <Link
                    to={item.link}
                    className="text-body-footer hover:text-white text-sm font-almarai transition-colors"
                  >
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company Column */}
          <div className={`${isAr ? 'sm:text-right' : 'sm:text-left'} text-center`}>
            <h3 className="mb-6 text-white font-ibm font-bold text-lg">{isAr ? 'الشركة' : 'Company'}</h3>
            <ul className="space-y-3">
              {companyLinks.map((item, i) => (
                <li key={i}>
                  <Link
                    to={item.link}
                    className="text-body-footer hover:text-white text-sm font-almarai transition-colors"
                  >
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Column */}
          <div className={`${isAr ? 'sm:text-right' : 'sm:text-left'} text-center`}>
            <h3 className="mb-6 text-white font-ibm font-bold text-lg">{isAr ? 'الدعم' : 'Support'}</h3>
            <ul className="space-y-3">
              {supportLinks.map((item, i) => (
                <li key={i}>
                  <Link
                    to={item.link}
                    className="text-body-footer hover:text-white text-sm font-almarai transition-colors"
                  >
                    {item.text}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-edge-dark pt-8 text-center">
          <p className="text-body-footer text-sm font-almarai">
            {'© ' + t.companyName + ' 2026 - ' + t.rights}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;